import { useTheme } from '@/src/context/ThemeContext';
import { useTranslation } from '@/src/i18n';
import { getPostBySlug } from '@/src/content/blog';
import { BlogPostScreen } from '@/src/presentation/screens/BlogPostScreen';
import Head from 'expo-router/head';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { StyleSheet, Text, View } from 'react-native';

export default function BlogPostContainer() {
  const { colors } = useTheme();
  const { language } = useTranslation();
  const router = useRouter();
  const { slug } = useLocalSearchParams<{ slug: string }>();

  const post = getPostBySlug(slug ?? '');

  if (!post) {
    return (
      <View style={[styles.center, { backgroundColor: colors.background }]}>
        <Text style={{ color: colors.error, fontSize: 18 }}>Post not found</Text>
      </View>
    );
  }

  const displayTitle = language === 'es' ? post.titleEs : post.title;
  const displaySummary = language === 'es' ? post.summaryEs : post.summary;
  const postUrl = `https://tournaly.app/blog/${post.slug}`;
  const ogImage = 'https://tournaly.app/og-image.png';

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: displayTitle,
    description: displaySummary,
    url: postUrl,
    datePublished: post.date,
    image: ogImage,
    keywords: post.tags.join(', '),
    author: {
      '@type': 'Organization',
      name: 'Tournaly',
      url: 'https://tournaly.app',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Tournaly',
      url: 'https://tournaly.app',
      logo: { '@type': 'ImageObject', url: ogImage },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
  });

  return (
    <>
      <Head>
        <title>{`${displayTitle} — Tournaly`}</title>
        <meta name="description" content={displaySummary} />
        <link rel="canonical" href={postUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Tournaly" />
        <meta property="og:title" content={displayTitle} />
        <meta property="og:description" content={displaySummary} />
        <meta property="og:url" content={postUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={displayTitle} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content="Tournaly" />
        {post.tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={displayTitle} />
        <meta name="twitter:description" content={displaySummary} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{jsonLd}</script>
      </Head>
      <BlogPostScreen
        post={post}
        colors={colors}
        onBack={() => router.push('/blog' as any)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
});
